import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Shield, Clock, Users, Star, Phone, MapPin } from 'lucide-react';


const CityLandingPage = ({ city, cityFr, region, seoKeyword, seoKeywordFr, trustStats }) => {
  const { language } = useLanguage();

  useEffect(() => {
    document.title = language === 'en'
      ? `Auto Financing ${city} | 2nd & 3rd Chance Credit | Credit Auto Reset`
      : `Financement Auto ${cityFr || city} | 2e et 3e Chance Credit | Credit Auto Reset`;
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? `Get approved for auto financing in ${city}, Quebec even with bad credit. 99% approval rate, $0 down payment. Credit Auto Reset serves ${city} and surrounding areas.`
        : `Obtenez votre financement auto a ${cityFr || city}, Quebec meme avec mauvais credit. 99% d'approbation, 0$ comptant. Credit Auto Reset dessert ${cityFr || city} et les environs.`
    );
  }, [language, city, cityFr]);

  const stats = trustStats || [
    { value: '99%', label: language === 'en' ? 'Approval Rate' : "Taux d'approbation" },
    { value: '0$', label: language === 'en' ? 'Down Payment' : 'Comptant requis' },
    { value: '24h', label: language === 'en' ? 'Response Time' : 'Temps de reponse' },
    { value: '1000+', label: language === 'en' ? 'Happy Clients' : 'Clients satisfaits' },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-[#1a5c3b] to-gray-900 py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(26,92,59,0.3),transparent_70%)]"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-green-400" />
            <span className="text-green-200 text-sm font-medium">{cityFr || city}, Quebec</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="city-h1">
            {language === 'en'
              ? `Auto Financing in ${city}`
              : `${seoKeywordFr || `Financement Auto ${cityFr || city}`}`}
          </h1>
          <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto mb-8">
            {language === 'en'
              ? `Bad credit? Bankruptcy? No problem. Credit Auto Reset specializes in 2nd and 3rd chance auto financing for ${city} residents.`
              : `Mauvais credit? Faillite? Aucun probleme. Credit Auto Reset est specialise en financement auto 2e et 3e chance pour les residents de ${cityFr || city}.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/demander-un-pret-auto">
              <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 font-bold text-base px-8 py-4" data-testid="city-cta-apply">
                {language === 'en' ? 'Apply Now - Free Evaluation' : 'Faire une demande - Evaluation gratuite'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-green-200 text-xs md:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Credit Auto Reset */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4">
            {language === 'en'
              ? `Why ${city} Residents Choose Credit Auto Reset`
              : `Pourquoi les residents de ${cityFr || city} choisissent Credit Auto Reset`}
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            {language === 'en'
              ? `We serve ${city} and all of ${region || 'Greater Montreal'} with specialized auto financing solutions.`
              : `Nous desservons ${cityFr || city} et tout${region ? ` ${region}` : 'e la grande region de Montreal'} avec des solutions de financement auto specialisees.`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: language === 'en' ? '99% Approval Rate' : "99% d'approbation",
                desc: language === 'en'
                  ? 'Even with bad credit, bankruptcy, or past refusals. We work with major banks and lenders across Canada.'
                  : "Meme avec mauvais credit, faillite ou refus anterieurs. Nous travaillons avec les grandes banques et preteurs au Canada."
              },
              {
                icon: Clock,
                title: language === 'en' ? 'Fast Response' : 'Reponse rapide',
                desc: language === 'en'
                  ? 'Get a response within 24 hours. Our analysts review your file and call you with options.'
                  : "Obtenez une reponse en 24 heures. Nos analystes examinent votre dossier et vous appellent avec les options."
              },
              {
                icon: Star,
                title: language === 'en' ? 'Credit Recovery' : 'Retablissement de credit',
                desc: language === 'en'
                  ? 'Our financing helps rebuild your credit score with every on-time payment.'
                  : "Notre financement aide a retablir votre cote de credit a chaque paiement effectue a temps."
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-[#1a5c3b] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {language === 'en'
              ? `Auto Financing Solutions in ${city}, Quebec`
              : `Solutions de financement auto a ${cityFr || city}, Quebec`}
          </h2>
          <div className="prose prose-lg text-gray-700 space-y-4">
            <p>
              {language === 'en'
                ? `Looking for auto financing in ${city}? Credit Auto Reset is Quebec's leading specialist in 2nd and 3rd chance credit for vehicle purchases. Whether you're dealing with bad credit, a recent bankruptcy, consumer proposal, or have been refused by traditional banks, we have solutions for you.`
                : `Vous cherchez un financement auto a ${cityFr || city}? Credit Auto Reset est le specialiste quebecois du financement 2e et 3e chance pour l'achat de vehicules. Que vous ayez un mauvais credit, une faillite recente, une proposition de consommateur ou que vous ayez ete refuse par les banques traditionnelles, nous avons des solutions pour vous.`}
            </p>
            <p>
              {language === 'en'
                ? `Our dealership is located in Montreal (Iles-des-Soeurs) and we proudly serve clients from ${city} and the entire ${region || 'Greater Montreal'} area. We work with most banks and financial institutions in Canada to find you the best financing terms possible.`
                : `Notre concessionnaire est situe a Montreal (Iles-des-Soeurs) et nous desservons fierement les clients de ${cityFr || city} et de tout${region ? ` ${region}` : 'e la region du Grand Montreal'}. Nous travaillons avec la plupart des banques et institutions financieres au Canada pour vous trouver les meilleures conditions de financement possibles.`}
            </p>
            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              {language === 'en' ? 'How It Works' : 'Comment ca fonctionne'}
            </h3>
            <ul className="space-y-2">
              {[
                language === 'en' ? 'Fill out our quick online application or chat with Alex, our AI assistant' : 'Remplissez notre demande en ligne rapide ou clavardez avec Alex, notre assistant',
                language === 'en' ? 'Our credit analyst reviews your file and calls you within 24 hours' : 'Notre analyste de credit examine votre dossier et vous appelle dans les 24 heures',
                language === 'en' ? 'Choose your vehicle from our selection of quality used cars, SUVs, and trucks' : 'Choisissez votre vehicule parmi notre selection de voitures, VUS et camions de qualite',
                language === 'en' ? 'Drive away with your new vehicle and start rebuilding your credit!' : 'Repartez avec votre nouveau vehicule et commencez a retablir votre credit!'
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#1a5c3b] mt-0.5 flex-shrink-0" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#1a5c3b] to-[#16a085]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {language === 'en'
              ? `Ready to Get Approved in ${city}?`
              : `Pret a etre approuve a ${cityFr || city}?`}
          </h2>
          <p className="text-white/90 mb-6">
            {language === 'en'
              ? 'Free evaluation, no obligation. Response within 24 hours.'
              : 'Evaluation gratuite, sans obligation. Reponse en 24 heures.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demander-un-pret-auto">
              <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 font-bold px-8">
                {language === 'en' ? 'Start Your Application' : 'Commencer votre demande'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:514-418-3968">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8">
                <Phone className="mr-2 w-5 h-5" />
                514-418-3968
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Internal Links - Related Resources */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {language === 'en' ? 'Helpful Resources' : 'Ressources utiles'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/faq" className="block p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-[#1a5c3b] mb-1">{language === 'en' ? 'FAQ' : 'Questions fréquentes'}</h3>
              <p className="text-sm text-gray-600">{language === 'en' ? 'Answers to common questions about auto financing' : 'Reponses aux questions courantes sur le financement auto'}</p>
            </Link>
            <Link to="/credit-analysis" className="block p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-[#1a5c3b] mb-1">{language === 'en' ? 'Free Credit Analysis' : 'Analyse de crédit gratuite'}</h3>
              <p className="text-sm text-gray-600">{language === 'en' ? 'Find out your financing options today' : 'Découvrez vos options de financement'}</p>
            </Link>
            <Link to="/blog" className="block p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-[#1a5c3b] mb-1">{language === 'en' ? 'Expert Articles' : 'Articles experts'}</h3>
              <p className="text-sm text-gray-600">{language === 'en' ? 'Tips on credit, financing and car buying' : 'Conseils sur le credit, financement et achat auto'}</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
